import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { useDocument } from '../../documents/DocumentContext';
import { DocumentSchema } from '../../documents/types';
import { PolicyDocumentView, PolicyDocumentMeta } from '../read/PolicyDocumentView';
import { interpolateVariables } from '../utils/interpolateVariables';

interface LivePreviewPanelProps {
    schema: DocumentSchema;
    title: string;
    variableMap: Record<string, string>;
    status: string;
    effectiveDate: string;
    expiryDate: string;
    tags: string[];
    authors: string[];
    attachments: string[];
}

export function LivePreviewPanel({
    schema,
    title,
    variableMap,
    status,
    effectiveDate,
    expiryDate,
    tags,
    authors,
    attachments,
}: LivePreviewPanelProps) {
    const { t } = useTranslation();
    const { data } = useDocument();

    const interpolatedData =
        Object.keys(variableMap).length > 0
            ? interpolateVariables(data, variableMap)
            : data;

    const meta: PolicyDocumentMeta = {
        title,
        status,
        effectiveDate,
        expiryDate,
        tags,
        authors,
        attachments,
    };

    return (
        <div className="sticky top-0">
            <div className="overflow-hidden rounded-lg border border-subtle bg-zinc-800/70">
                <div className="border-b border-subtle px-8 py-5">
                    <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground/80">
                        <Eye className="h-3 w-3" />
                        {t('departmentPolicies.editor.livePreview')}
                    </p>
                </div>
                <div className="min-h-[calc(100vh-8rem)] px-8 py-8">
                    <PolicyDocumentView
                        schema={schema}
                        data={interpolatedData}
                        meta={meta}
                        hideToolbar
                    />
                </div>
            </div>
        </div>
    );
}
